/**
 * Monatsansicht-Kalender als 7×N-Grid (Mo–So).
 *
 * Zeigt Schichten und Kalender-Einträge (Notiz/Frei/Urlaub/Krank/Wunschfrei).
 * Einträge sind Tages-Marker (kein Lohn); ein Klick darauf öffnet — wie ein
 * Klick auf den Tag — die Tages-Detailansicht.
 */

import { useMemo } from 'react';
import type { LocalShift, LocalLocation, LocalCalendarEntry } from '../db/schema';
import { ENTRY_TYPE_META } from '../lib/calendarEntries';

interface Props {
  month: string;
  shifts: LocalShift[];
  entries?: LocalCalendarEntry[];
  locationsById: Map<string, LocalLocation>;
  onDayClick: (isoDate: string) => void;
  onShiftClick: (shift: LocalShift) => void;
}

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

interface DayCell {
  iso: string;
  day: number;
  inCurrentMonth: boolean;
  isToday: boolean;
}

function buildGrid(monthKey: string): DayCell[] {
  const [y, m] = monthKey.split('-').map(Number);
  const yy = y!;
  const mm = (m ?? 1) - 1;
  const first = new Date(Date.UTC(yy, mm, 1));
  const offset = (first.getUTCDay() + 6) % 7;
  const start = new Date(Date.UTC(yy, mm, 1 - offset));

  const cells: DayCell[] = [];
  // Gesamtprüfung 11.09.2026: „heute“ nach lokalem Datum (nicht UTC).
  const now = new Date();
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    const iso = d.toISOString().slice(0, 10);
    cells.push({
      iso,
      day: d.getUTCDate(),
      inCurrentMonth: d.getUTCMonth() === mm,
      isToday: iso === todayIso,
    });
  }
  while (cells.length > 35 && cells.slice(-7).every((c) => !c.inCurrentMonth)) {
    cells.length = cells.length - 7;
  }
  return cells;
}

export function MonthCalendar({
  month,
  shifts,
  entries = [],
  locationsById,
  onDayClick,
  onShiftClick,
}: Props) {
  const grid = useMemo(() => buildGrid(month), [month]);

  const byDate = useMemo(() => {
    const map = new Map<string, LocalShift[]>();
    for (const s of shifts) {
      const arr = map.get(s.shiftDate) ?? [];
      arr.push(s);
      map.set(s.shiftDate, arr);
    }
    return map;
  }, [shifts]);

  const entriesByDate = useMemo(() => {
    const map = new Map<string, LocalCalendarEntry[]>();
    for (const e of entries) {
      const arr = map.get(e.date) ?? [];
      arr.push(e);
      map.set(e.date, arr);
    }
    return map;
  }, [entries]);

  return (
    <div className="bg-surface rounded-lg overflow-hidden text-sm border border-border/60">
      <div className="grid grid-cols-7 bg-slate-900 text-[11px] sm:text-xs text-slate-400">
        {WEEKDAYS.map((wd) => (
          <div key={wd} className="px-0.5 sm:px-1 py-1.5 sm:py-2 text-center font-medium">
            {wd}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {grid.map((cell) => {
          const dayShifts = byDate.get(cell.iso) ?? [];
          const dayEntries = entriesByDate.get(cell.iso) ?? [];
          const shownEntries = dayEntries.slice(0, 2);
          const slots = Math.max(0, 3 - shownEntries.length);
          const shownShifts = dayShifts.slice(0, slots);
          const overflow =
            dayEntries.length - shownEntries.length + (dayShifts.length - shownShifts.length);
          return (
            <button
              key={cell.iso}
              onClick={() => onDayClick(cell.iso)}
              className={[
                'border border-slate-700/40 p-0.5 sm:p-1 text-left flex flex-col gap-0.5',
                'hover:bg-slate-700/40 transition !min-h-[64px] sm:!min-h-[80px]',
                !cell.inCurrentMonth ? 'bg-slate-900/30 text-slate-500' : '',
                cell.isToday ? 'ring-2 ring-brand ring-inset' : '',
              ].join(' ')}
              aria-label={`Tag ${cell.iso}`}
            >
              <span className={`text-xs ${cell.isToday ? 'font-bold text-brand' : 'text-slate-300'}`}>
                {cell.day}
              </span>

              {shownEntries.map((e) => {
                const meta = ENTRY_TYPE_META[e.type];
                return (
                  <span
                    key={e.id}
                    className={[
                      'block text-[9px] sm:text-[10px] truncate px-0.5 sm:px-1 rounded',
                      e.pending ? 'ring-1 ring-amber-400/50' : '',
                      e.status === 'rejected' ? 'line-through opacity-60' : '',
                    ].join(' ')}
                    style={{
                      backgroundColor: `${meta.color}33`,
                      borderLeft: `3px solid ${meta.color}`,
                      color: '#e6ebf3',
                    }}
                    title={`${meta.label}${e.title ? ` · ${e.title}` : ''}${e.halfDay ? ' (halber Tag)' : ''}`}
                  >
                    {e.title || meta.short}
                  </span>
                );
              })}

              {shownShifts.map((s) => {
                const loc = locationsById.get(s.locationId);
                return (
                  <span
                    key={s.id}
                    role="button"
                    tabIndex={0}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      onShiftClick(s);
                    }}
                    onKeyDown={(ev) => {
                      if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.stopPropagation();
                        onShiftClick(s);
                      }
                    }}
                    className={[
                      'block text-[9px] sm:text-[10px] truncate px-0.5 sm:px-1 rounded cursor-pointer',
                      s.status === 'cancelled' ? 'line-through opacity-60' : '',
                      s.status === 'planned' ? 'opacity-90' : '',
                      s.pending ? 'ring-1 ring-amber-400/50' : '',
                    ].join(' ')}
                    style={{
                      backgroundColor: (loc?.color ?? '#475569') + '40',
                      borderLeft: `3px solid ${loc?.color ?? '#475569'}`,
                    }}
                    title={`${loc?.name ?? '?'} · ${s.startTime}–${s.endTime}${s.pending ? ' (ungesynct)' : ''}`}
                  >
                    <span className="hidden sm:inline">{s.startTime} </span>
                    {loc?.name ?? '?'}
                  </span>
                );
              })}

              {overflow > 0 && (
                <span className="text-[9px] sm:text-[10px] text-slate-400">+{overflow}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
